import { useEffect, useState } from "react";
import { Sparkles, Lock, Check } from "lucide-react";
import api from "../lib/api";

const SKINS = [
  {
    id: "default",
    name: "Nova Violet",
    desc: "Default worm paddle",
    colorFrom: "#d462ff",
    colorTo: "#7a1fb8",
    requires: "Always unlocked",
  },
  {
    id: "rainbow",
    name: "Prism Trail",
    desc: "Top 10 all-time pilots",
    colorFrom: "#00ffff",
    colorTo: "#ff007f",
    requires: "Rank ≤ 10",
  },
  {
    id: "gold",
    name: "Solar Gold",
    desc: "Top 3 all-time pilots",
    colorFrom: "#ffd700",
    colorTo: "#ff8c1a",
    requires: "Rank ≤ 3",
  },
  {
    id: "beginner-victor",
    name: "Azure Initiate",
    desc: "Cleared Beginner sector",
    colorFrom: "#a0ffff",
    colorTo: "#0080b3",
    requires: "Beat level 500",
  },
  {
    id: "easy-champion",
    name: "Emerald Champion",
    desc: "Cleared Easy sector",
    colorFrom: "#a0ffb5",
    colorTo: "#007a26",
    requires: "Beat level 1000",
  },
  {
    id: "medium-conqueror",
    name: "Solar Conqueror",
    desc: "Cleared Medium sector",
    colorFrom: "#fff2a0",
    colorTo: "#805f00",
    requires: "Beat level 1500",
  },
  {
    id: "hard-warrior",
    name: "Ember Warrior",
    desc: "Cleared Hard sector",
    colorFrom: "#ffc78a",
    colorTo: "#7a3b00",
    requires: "Beat level 2000",
  },
  {
    id: "expert-master",
    name: "Crimson Master",
    desc: "Cleared Expert sector",
    colorFrom: "#ff80bf",
    colorTo: "#800040",
    requires: "Beat level 2500",
  },
  {
    id: "legendary-ascended",
    name: "Void Ascendant",
    desc: "Cleared Legendary sector",
    colorFrom: "#e8aaff",
    colorTo: "#4a0080",
    requires: "Beat level 3000",
  },
];

export default function SkinSelector({ onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    api
      .get("/cosmetics")
      .then((r) => !cancel && setState(r.data))
      .catch(() => !cancel && setState({ rank: 0, unlocked: ["default"], active: "default" }));
    return () => {
      cancel = true;
    };
  }, []);

  const pick = async (id) => {
    if (busy || !state) return;
    if (!state.unlocked.includes(id)) return;
    setBusy(true);
    try {
      const r = await api.post("/cosmetics/active", { skin: id });
      setState({ ...state, ...r.data });
      onChanged?.(r.data.active);
    } catch (_) {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  return (
    <div className="glass p-6" data-testid="skin-selector">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#ffd700]" />
          <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
            Worm Skins
          </div>
        </div>
        <div className="font-mono-cosmic text-[9px] tracking-[0.3em] text-white/40 uppercase">
          {state.rank > 0 ? `Rank #${state.rank}` : "Unranked"}
        </div>
      </div>
      <div className="space-y-3">
        {SKINS.map((s) => {
          const isUnlocked = state.unlocked.includes(s.id);
          const isActive = state.active === s.id;
          return (
            <button
              key={s.id}
              onClick={() => pick(s.id)}
              disabled={!isUnlocked || busy}
              className={`w-full text-left border p-3 transition flex items-center gap-3 ${
                isActive
                  ? "border-[#00ffff] shadow-[0_0_14px_rgba(0,255,255,0.25)]"
                  : isUnlocked
                    ? "border-white/15 hover:border-white/40"
                    : "border-white/10 opacity-50 cursor-not-allowed"
              }`}
              data-testid={`skin-${s.id}`}
            >
              <div
                className="w-14 h-6 rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${s.colorFrom}, ${s.colorTo})`,
                  boxShadow: `0 0 12px ${s.colorFrom}66`,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-heading uppercase tracking-wider text-sm text-white truncate">
                  {s.name}
                </div>
                <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50">
                  {isUnlocked ? s.desc : s.requires}
                </div>
              </div>
              {isActive ? (
                <Check className="w-4 h-4 text-[#00ffff]" />
              ) : isUnlocked ? (
                <span className="font-mono-cosmic text-[10px] tracking-[0.25em] text-[#00ffff] uppercase">
                  Equip
                </span>
              ) : (
                <Lock className="w-3.5 h-3.5 text-white/40" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
